import { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";

import DropdownForm from "../Form/DropdownForm";
import FormField from "../Form/FormField";
import TextareaForm from "../Form/TextareaForm";
import {
  ARABIC_REGEX,
  BOOLEAN_CHOICES,
  E_MONEY_VALUE,
  ENGLISH_REGEX,
  IS_ACTIVE,
} from "../../constants/globals";

import FileInput from "../Form/FileInput";
import {
  createNewCourseForAdmin,
  updateCourseForAdmin,
  getCourseTypes,
  getInstructorsDropdown,
  getLessonsLevels,
  getPrerequisiteCourses,
} from "../../apis";
import Swal from "sweetalert2";
import RowLoader from "../UI/RowLoader";
import { useNavigate, useParams } from "react-router-dom";

const AddOrEditCourse = ({
  mode = "add",
  initialValuesForEdit,
  imageForEdit = "",
}) => {
  const navigate = useNavigate();
  const { courseId } = useParams();
  const [image, setImage] = useState(imageForEdit);
  const [courseLevel, setCourseLevel] = useState([]);
  const [courseTypes, setCourseTypes] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [prerequisiteCourses, setPrerequisiteCourses] = useState([]);

  // Fetch Data Once When Component Mounts
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [
          courseTypesData,
          courseLevelsData,
          instructorsData,
          prerequisiteCoursesData,
        ] = await Promise.all([
          getCourseTypes(),
          getLessonsLevels(),
          getInstructorsDropdown(),
          getPrerequisiteCourses(),
        ]);
        const handleCourseTypes = courseTypesData.data.map((d) => ({
          title: d,
          id: d,
        }));
        const handleCourseLevels = courseLevelsData.data.map((d) => ({
          title: d,
          id: d,
        }));
        setCourseTypes(handleCourseTypes || []);
        setCourseLevel(handleCourseLevels || []);
        setInstructors(instructorsData.data);
        setPrerequisiteCourses([
          ...prerequisiteCoursesData.data,
          {
            id: "",
            course_name_en: "None",
          },
        ]);
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    };

    fetchData();
  }, []);

  // Form Validation Schema
  const validationSchema = Yup.object().shape({
    titleEn: Yup.string()
      .matches(ENGLISH_REGEX, "Only English letters are allowed")
      .min(2, "Too Short!")
      .max(200, "Too Long!")
      .required("Required"),
    titleAr: Yup.string()
      .matches(ARABIC_REGEX, "Only Arabic letters are allowed")
      .min(2, "Too Short!")
      .max(200, "Too Long!")
      .required("Required"),
    descriptionEn: Yup.string()
      .matches(ENGLISH_REGEX, "Only English letters are allowed")
      .min(2, "Too Short!")
      .max(400, "Too Long!")
      .required("Required"),
    descriptionAr: Yup.string()
      .matches(ARABIC_REGEX, "Only Arabic letters are allowed")
      .min(2, "Too Short!")
      .max(400, "Too Long!")
      .required("Required"),
    courseType: Yup.string().required("Required"),
    courseLevel: Yup.string().required("Required"),

    coursePriceEm: Yup.number()
      .typeError("Price must be a number")
      .positive("Price can’t be less than zero")
      .required("Required"),

    pvUplinerValue: Yup.number()
      .typeError("Pv Upliner must be a number")
      .positive("Price can’t be less than zero")
      .required("Required"),
    directEMoney: Yup.number()
      .typeError("Direct E.Money must be a number")
      .positive("Price can’t be less than zero")
      .required("Required"),

    instructorName: Yup.string().required("Required"),
    isDisplayedToPartner: Yup.string().required("Required"),
    isSitePublished: Yup.string().required("Required"),
    courseRating: Yup.string().required("Required"),
    isActive: Yup.string().required("Required"),

    confirm: Yup.boolean().oneOf([true], "You must confirm your choices"),
    promoVideo: Yup.string()
      .required("Promo Video Link is required")
      .url("Invalid URL format"),
    image: Yup.mixed()
      .test("isRequired", "Image is required", function (value) {
        if (mode === "edit") return true;
        return !!value;
      })
      .test("fileType", "Invalid file format", function (value) {
        if (!value) return true; // Handle optional file input
        const allowedExtensions = ["pdf", "png", "jpg", "jpeg"];
        const fileExtension = value?.name?.split(".").pop().toLowerCase();
        return allowedExtensions.includes(fileExtension);
      })
      .test("fileSize", "File size must be less than 5MB", function (value) {
        if (!value) return true; // Handle optional file input
        const maxSize = 5 * 1024 * 1024; // 5MB in bytes
        return value?.size <= maxSize;
      }),
  });

  const handleRemoveImage = (name, formField) => {
    setImage("");
    formField(name, "");
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    const handleIntialValues = { ...initialValues };
    delete handleIntialValues.confirm;
    const handleValues = { ...values };
    delete handleValues.confirm;
    if (mode === "edit") {
      if (JSON.stringify(handleIntialValues) === JSON.stringify(handleValues)) {
        Swal.fire({
          title: "No changes detected",
          text: "No changes were made to the course, no update was made.",
          icon: "info",
        });
        return;
      }
    }
    try {
      // Create FormData object
      const formData = new FormData();

      // Map Formik values to the API expected fields
      if (mode === "edit") {
        formData.append("_method", "patch");
      }
      formData.append("course_name_arabic", values.titleAr);
      formData.append("course_name_english", values.titleEn);
      formData.append("course_description_arabic", values.descriptionAr);
      formData.append("course_description_english", values.descriptionEn);
      formData.append("course_type", values.courseType);
      formData.append("course_level", values.courseLevel);
      formData.append("course_price_em", values.coursePriceEm);
      formData.append("course_price_eg", +values.coursePriceEm * E_MONEY_VALUE);
      formData.append("pv_upliner_value", values.pvUplinerValue);
      formData.append("direct_em", values.directEMoney);
      formData.append("prerequisite_course", values.prerequisiteCourse);
      formData.append("instructor", values.instructorName);
      formData.append("is_partner_displayed", values.isDisplayedToPartner);
      formData.append("is_site_published", values.isSitePublished);
      formData.append("promo_video", values.promoVideo);

      formData.append("course_rating", values.courseRating);
      formData.append("is_active", values.isActive);
      if (values.image) {
        formData.append("image", values.image);
      }

      // Call API function
      if (mode === "edit") {
        await updateCourseForAdmin(courseId, formData);
      } else {
        await createNewCourseForAdmin(formData);
      }

      // Show success alert
      Swal.fire({
        title: "Success!",
        text: "Course submited successfully.",
        icon: "success",
        confirmButtonText: "OK",
      });

      resetForm(); // Reset form after successful submission
      setImage(""); // reset image
      navigate("/courses-management");
    } catch (error) {
      console.error("Error submitting form:", error);

      // Show error alert
      Swal.fire({
        title: "Error!",
        text: "Something went wrong. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setSubmitting(false); // Stop form submitting state
    }
  };
  const initialValues =
    mode === "edit"
      ? initialValuesForEdit
      : {
          titleEn: "",
          titleAr: "",
          descriptionEn: "",
          descriptionAr: "",
          courseType: "",
          courseLevel: "",
          coursePriceEm: "",
          coursePriceEg: "",
          pvUplinerValue: "",
          directEMoney: "",
          prerequisiteCourse: "",
          instructorName: "",
          isDisplayedToPartner: "1",
          isSitePublished: "1",
          courseRating: "",
          isActive: "1",
          image: "",
          confirm: false,
          promoVideo: "",
        };

  return (
    <>
      <p className="tw-text-[#BF0404] tw-text-end tw-mb-1">
        * means this field is required
      </p>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          errors,
          touched,
          handleChange,
          handleBlur,
          values,
          setFieldValue,
          isSubmitting,
        }) => (
          <Form className="card !tw-rounded-xl">
            <div className="card-body tw-shadow-xl tw-rounded-xl">
              <div className="row">
                {/* Course Title English */}
                <FormField
                  name="titleEn"
                  label="Course Title English"
                  placeholder="Course Title English"
                  touched={touched}
                  errors={errors}
                  col_md="6"
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                  value={values.titleEn}
                />

                {/* Course Title Arabic */}
                <FormField
                  name="titleAr"
                  label="Course Title Arabic"
                  placeholder="Course Title Arabic"
                  touched={touched}
                  errors={errors}
                  col_md="6"
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                  value={values.titleAr}
                />

                {/* Course Description English  */}
                <TextareaForm
                  name="descriptionEn"
                  placeholder="Course Description English "
                  touched={touched.descriptionEn}
                  errors={errors.descriptionEn}
                  handleBlur={handleBlur}
                  label="Course Description English "
                />

                {/* Course Description Arabic */}
                <TextareaForm
                  name="descriptionAr"
                  placeholder="Course Description Arabic"
                  touched={touched.descriptionAr}
                  errors={errors.descriptionAr}
                  handleBlur={handleBlur}
                  label="Course Description Arabic"
                />

                {/* courseType */}
                <DropdownForm
                  handleChange={handleChange}
                  name="courseType"
                  dataToDrop={courseTypes}
                  error={errors.courseType}
                  touched={touched.courseType}
                  label="Course Type"
                  placeholder="Course Type"
                  gridStyle="col-sm-6"
                />

                {/* courseLevel */}
                <DropdownForm
                  handleChange={handleChange}
                  name="courseLevel"
                  dataToDrop={courseLevel}
                  error={errors.courseLevel}
                  touched={touched.courseLevel}
                  label="Course Level"
                  placeholder="Course Level"
                  gridStyle="col-sm-6"
                />

                {/* Course Price E.M */}
                <FormField
                  type="number"
                  name="coursePriceEm"
                  label="Course Price (E.M)"
                  placeholder="Course Price (E.M)"
                  touched={touched}
                  errors={errors}
                  col_md="6"
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                  value={values.coursePriceEm}
                />
                {/* Course Price E.G */}
                <FormField
                  readOnly
                  type="number"
                  name="coursePriceEg"
                  label="Course Price (E.G)"
                  placeholder="Course Price (E.G)"
                  touched={touched}
                  errors={errors}
                  col_md="6"
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                  value={
                    +values.coursePriceEm * E_MONEY_VALUE > 0
                      ? +values.coursePriceEm * E_MONEY_VALUE
                      : 0
                  }
                />

                {/* PV Upliner Value */}
                <FormField
                  type="number"
                  name="pvUplinerValue"
                  label="PV Upliner Value"
                  placeholder="PV Upliner Value"
                  touched={touched}
                  errors={errors}
                  col_md="6"
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                  value={values.pvUplinerValue}
                />

                {/* Direct E.money */}
                <FormField
                  type="number"
                  name="directEMoney"
                  label="Direct E.money"
                  placeholder="Direct E.money"
                  touched={touched}
                  errors={errors}
                  col_md="6"
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                  value={values.directEMoney}
                />

                {/*Prerequisite Course */}
                <DropdownForm
                  required={false}
                  handleChange={handleChange}
                  name="prerequisiteCourse"
                  dataToDrop={prerequisiteCourses}
                  error={errors.prerequisiteCourse}
                  touched={touched.prerequisiteCourse}
                  label="Prerequisite Course"
                  placeholder="Prerequisite Course"
                  gridStyle="col-sm-6"
                  labelKey="course_name_en"
                />
                {/*Instructor Name */}
                <DropdownForm
                  handleChange={handleChange}
                  name="instructorName"
                  dataToDrop={instructors}
                  error={errors.instructorName}
                  touched={touched.instructorName}
                  label="Instructor Name"
                  placeholder="Instructor Name"
                  gridStyle="col-sm-6"
                  labelKey="name_en"
                />

                {/* Is Displayed To Partner */}
                <DropdownForm
                  handleChange={handleChange}
                  name="isDisplayedToPartner"
                  dataToDrop={BOOLEAN_CHOICES}
                  error={errors.isDisplayedToPartner}
                  touched={touched.isDisplayedToPartner}
                  label="Is Displayed To Partner"
                  placeholder="Is Displayed To Partner"
                  gridStyle="col-sm-6"
                />

                {/* Is Site Published */}
                <DropdownForm
                  handleChange={handleChange}
                  name="isSitePublished"
                  dataToDrop={BOOLEAN_CHOICES}
                  error={errors.isSitePublished}
                  touched={touched.isSitePublished}
                  label="Is Site Published"
                  placeholder="Is Site Published"
                  gridStyle="col-sm-6"
                />

                {/* Course Rating */}
                <DropdownForm
                  handleChange={handleChange}
                  name="courseRating"
                  dataToDrop={Array.from({ length: 6 }, (_, i) => ({
                    title: (i + 1).toString(),
                    id: i + 1,
                  }))}
                  error={errors.courseRating}
                  touched={touched.courseRating}
                  label="Course Rating"
                  placeholder="Course Rating"
                  gridStyle="col-sm-6"
                />

                {/*isActive */}
                <DropdownForm
                  handleChange={handleChange}
                  name="isActive"
                  dataToDrop={IS_ACTIVE}
                  error={errors.isActive}
                  touched={touched.isActive}
                  label="is Active"
                  placeholder="Is Active"
                  gridStyle="col-sm-6"
                />

                {/* Textarea for promo video Link */}
                <TextareaForm
                  name="promoVideo"
                  placeholder="Promo Video Link"
                  touched={touched.promoVideo}
                  errors={errors.promoVideo}
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  label="Promo Video Link"
                />
                {/* Image */}
                <FileInput
                  name="image"
                  label="Upload Course Image"
                  value={image}
                  onChange={setFieldValue}
                  onRemove={handleRemoveImage}
                  error={errors.image}
                  touched={touched.image}
                  setPhoto={setImage}
                />

                {/* confirm */}
                <div className="col-md-12 tw-flex tw-items-center">
                  <input
                    type="checkbox"
                    name="confirm"
                    id="confirm"
                    checked={values.confirm}
                    onChange={handleChange}
                    className="!tw-mr-2 tw-w-4 "
                  />
                  <label
                    htmlFor="confirm"
                    className="tw-text-sm tw-cursor-pointer !tw-m-0 !tw-mt-1"
                  >
                    I confirm that, the Course data is correct.
                  </label>
                </div>
                {errors.confirm && touched.confirm && (
                  <p className="tw-text-red-500 tw-text-xs tw-mt-1">
                    {errors.confirm}
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <div className="tw-mt-4 tw-text-right">
                <button
                  type="submit"
                  className="!tw-bg-primary btn tw-text-white !tw-w-[100px] hover:!tw-bg-primary/90 hover:tw-text-white"
                >
                  {isSubmitting ? (
                    <RowLoader paddingStyle="tw-py-0" />
                  ) : (
                    "Submit"
                  )}
                </button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default AddOrEditCourse;
